import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Check, Bath, PaintBucket, Home as HomeIcon, Hammer,
  Building2, ChefHat, ShieldCheck, MapPin, FileText, Award, Clock,
  Users, Star, ChevronDown, ChevronUp, Phone, CheckCircle, Loader2, Wrench
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import { leadFormSchema } from '../lib/validations';
import { submitLead } from '../lib/api';

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'Renovation', href: '/renovation', current: true }
];

const serviceCards = [
  { id: 1, title: 'Kitchen Renovations', description: 'Transform your kitchen with custom cabinetry, modern countertops, and energy-efficient appliances. Create the heart of your home.', icon: ChefHat },
  { id: 2, title: 'Bathroom Remodels', description: 'Spa-like retreats with luxury fixtures, walk-in showers, and elegant tile work. Upgrade your daily routine.', icon: Bath },
  { id: 3, title: 'Flooring & Painting', description: 'Premium hardwood, tile, and designer paint finishes that breathe new life into every room of your home.', icon: PaintBucket },
  { id: 4, title: 'ADU + Garage Conversions', description: 'Maximize your property value with accessory dwelling units and converted living spaces. LA permit specialists.', icon: HomeIcon },
  { id: 5, title: 'Roof & Exterior Repairs', description: 'Protect your investment with professional roofing, siding, and exterior restoration services.', icon: Hammer },
  { id: 6, title: 'Whole-Home Renovations', description: 'Complete home transformations from floor plans to finishing touches. Your vision, expertly executed.', icon: Building2 }
];

const trustPoints = [
  { id: 1, title: 'Licensed & Insured', description: 'Fully licensed contractors with comprehensive insurance coverage for your peace of mind.', icon: ShieldCheck },
  { id: 2, title: 'Local LA Renovation Experts', description: 'Deep knowledge of Los Angeles building codes, permits, and neighborhood requirements.', icon: MapPin },
  { id: 3, title: 'Transparent, Itemized Pricing', description: 'No hidden fees. Detailed quotes with clear breakdowns so you know exactly where your investment goes.', icon: FileText },
  { id: 4, title: 'High-Quality Craftsmanship', description: 'Premium materials and skilled artisans who take pride in every detail of your renovation.', icon: Award },
  { id: 5, title: 'On-Time Delivery', description: 'Respect for your schedule with realistic timelines and consistent progress updates.', icon: Clock },
  { id: 6, title: 'Vetted Contractor Network', description: 'Every contractor in our network is background-checked, reviewed, and held to the highest standards.', icon: Users }
];

const howItWorksSteps = [
  { step: 1, title: 'Tell us about your project', description: 'We collect your goals, needs, and budget.' },
  { step: 2, title: 'Receive a detailed quote', description: 'Transparent, itemized estimate \u2014 no hidden costs.' },
  { step: 3, title: 'Transform your home', description: 'Licensed pros deliver high-quality work on your schedule.' }
];

const testimonials = [
  { id: 1, title: 'Kitchen Transformation', location: 'West LA', description: 'Many homeowners tell us they love how we modernize outdated kitchens with new cabinetry, lighting, and open layouts \u2014 all delivered with clear timelines and transparent pricing.' },
  { id: 2, title: 'Bathroom Upgrade', location: 'Valley Village', description: 'Clients frequently share that they\'re impressed by our attention to detail in bathroom remodels, from tilework to fixtures to maximizing small spaces.' },
  { id: 3, title: 'Whole-Home Refresh', location: 'Pasadena', description: 'Homeowners often mention how smooth the process feels when updating multiple rooms at once, thanks to consistent communication and a dedicated project manager.' }
];

const faqItems = [
  { id: 1, question: 'How long does a kitchen remodel take?', answer: 'A typical kitchen remodel takes 4-8 weeks depending on the scope. Minor updates like cabinet refacing can be done in 1-2 weeks, while complete gut renovations may take 8-12 weeks. We provide detailed timelines during your free consultation.' },
  { id: 2, question: 'Do you assist with permits?', answer: 'Yes! We handle all permit applications and inspections for your project. Our team is well-versed in Los Angeles building codes and zoning requirements, ensuring your renovation is fully compliant.' },
  { id: 3, question: 'Can I get an itemized cost breakdown?', answer: 'Absolutely. Transparency is core to our process. Every quote includes detailed line items for materials, labor, permits, and any subcontractor work. No hidden fees or surprise charges.' },
  { id: 4, question: 'What areas of LA do you service?', answer: 'We serve all of Los Angeles County including Santa Monica, Beverly Hills, Pasadena, Glendale, Burbank, West Hollywood, Venice, Mar Vista, Silver Lake, and surrounding communities.' },
  { id: 5, question: "What's included in the free estimate?", answer: 'Our free estimate includes an on-site consultation, detailed project scope assessment, material recommendations, timeline projection, and a comprehensive written quote with no obligation.' },
  { id: 6, question: 'Do you offer financing options?', answer: 'Yes, we partner with trusted lenders to offer flexible financing solutions. Whether you need a home equity line, renovation loan, or payment plan, we can help connect you with the right option.' }
];

const renovationTypeOptions = [
  'Kitchen', 'Bathroom', 'Flooring', 'Exterior/Roof', 'ADU/Garage Conversion', 'Whole-Home Remodel'
];

const budgetOptions = [
  { value: '', label: 'Select your budget range' },
  { value: 'under-10k', label: 'Under $10k' },
  { value: '10k-25k', label: '$10k\u2013$25k' },
  { value: '25k-50k', label: '$25k\u2013$50k' },
  { value: '50k-100k', label: '$50k\u2013$100k' },
  { value: '100k-plus', label: '$100k+' }
];

const timelineOptions = [
  { value: '', label: 'Select your timeline' },
  { value: 'asap', label: 'ASAP' },
  { value: '1-3-months', label: '1\u20133 months' },
  { value: '3-6-months', label: '3\u20136 months' },
  { value: 'researching', label: 'Just researching' }
];

const conditionOptions = [
  { value: '', label: 'Select property condition' },
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'needs-major-work', label: 'Needs major work' }
];

const RenovationPage = () => {
  const [openFaqs, setOpenFaqs] = useState(new Set());
  const form = useForm({
    resolver: zodResolver(leadFormSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      property_address: "",
      sms_consent: false,
    },
  });

  const [enrichment, setEnrichment] = useState({
    renovationTypes: [],
    budget: "",
    timeline: "",
    condition: "",
    projectDescription: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const scrollToForm = (e) => {
    if (e) e.preventDefault();
    const formSection = document.getElementById('get-quote');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        const firstInput = document.getElementById('first_name');
        if (firstInput) firstInput.focus();
      }, 600);
    }
  };

  // Auto-scroll to form when arriving with #get-quote hash (from external links)
  React.useEffect(() => {
    if (window.location.hash === '#get-quote') {
      setTimeout(() => scrollToForm(), 300);
    }
  }, []);

  const toggleFaq = (id) => {
    setOpenFaqs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleRenovationType = (type) => {
    setEnrichment((prev) => {
      const types = prev.renovationTypes.includes(type)
        ? prev.renovationTypes.filter((t) => t !== type)
        : [...prev.renovationTypes, type];
      return { ...prev, renovationTypes: types };
    });
  };

  const handleEnrichmentChange = (e) => {
    const { name, value } = e.target;
    setEnrichment((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (validatedValues) => {
    try {
      await submitLead({
        ...validatedValues,
        lead_type: "renovate",
        lead_source: "renovation",
        renovation_types: enrichment.renovationTypes,
        budget: enrichment.budget,
        timeline: enrichment.timeline,
        condition: enrichment.condition,
        project_description: enrichment.projectDescription,
      });
      setIsSubmitted(true);
      toast.success("Your quote request has been submitted!");
    } catch (error) {
      if (error.response?.status === 422) {
        const serverErrors = error.response.data.errors;
        if (Array.isArray(serverErrors)) {
          serverErrors.forEach(({ field, message }) => {
            if (form.getValues(field) !== undefined) {
              form.setError(field, { type: "server", message });
            }
          });
        }
      } else {
        toast.error("Something went wrong. Please try again later.");
      }
    }
  };

  const inputBaseClass = "w-full px-4 py-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#D6A94F] focus:border-transparent transition-colors";

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1F3B60' }}>
      <Helmet>
        <title>Renovation Services | Pacific Crest Properties — Los Angeles</title>
        <meta name="description" content="Professional home renovation services in Los Angeles. Kitchen remodels, bathroom upgrades, flooring, painting, and whole-home improvements with transparent pricing." />
        <meta name="keywords" content="Los Angeles renovation services, kitchen remodel LA, bathroom renovation Los Angeles, home improvement LA, renovation contractor" />
        <link rel="canonical" href="https://renovation.pacificcrestprop.com" />
        <meta property="og:title" content="Renovation Services | Pacific Crest Properties" />
        <meta property="og:description" content="Transform your Los Angeles home with professional renovation services." />
        <meta property="og:url" content="https://renovation.pacificcrestprop.com" />
      </Helmet>

      <Header />
      <div className="pt-20">
        <Breadcrumb items={breadcrumbItems} variant="dark" />
      </div>

      {/* Hero Section */}
      <section className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block font-semibold text-xs tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full" style={{ color: '#D6A94F', backgroundColor: 'rgba(214, 169, 79, 0.1)', border: '1px solid rgba(214, 169, 79, 0.2)' }}>
            Los Angeles' Trusted Renovation Experts
          </span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            Home Renovation & Remodeling in Los Angeles
          </h1>
          <div className="divider-gold mx-auto mb-8" />
          <p className="text-xl text-white/70 leading-relaxed max-w-3xl mx-auto mb-6">
            Modern upgrades, expert craftsmanship, and clear pricing — get your personalized renovation quote today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <a
              href="#get-quote"
              onClick={scrollToForm}
              className="btn-gold inline-flex items-center space-x-2 text-lg group animate-pulse-glow"
            >
              <span>Get My Renovation Quote</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="tel:+13105966534"
              className="btn-outline-gold inline-flex items-center space-x-2 text-lg"
            >
              <Phone className="w-5 h-5" />
              <span>Call (310) 596-6534</span>
            </a>
          </div>
          <p className="text-sm text-white/50">
            No obligation &bull; Free estimate &bull; Your information is secure
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block font-semibold text-xs tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full" style={{ color: '#D6A94F', backgroundColor: 'rgba(214, 169, 79, 0.1)', border: '1px solid rgba(214, 169, 79, 0.2)' }}>
              Our Services
            </span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4" style={{ color: '#1F3B60' }}>
              Expert Renovation Services
            </h2>
            <p className="text-lg mt-4 max-w-2xl mx-auto" style={{ color: '#3A5A85' }}>
              From kitchen remodels to whole-home transformations, we deliver quality craftsmanship across Los Angeles.
            </p>
            <div className="divider-gold mx-auto mt-6" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceCards.map((service) => {
              const IconComponent = service.icon;
              return (
                <div key={service.id} className="bg-white rounded-2xl p-6 shadow-md text-center hover:shadow-lg transition-shadow duration-300">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                    <IconComponent className="w-6 h-6" style={{ color: '#1F3B60' }} />
                  </div>
                  <h3 className="font-display font-bold text-lg mb-2" style={{ color: '#1F3B60' }}>{service.title}</h3>
                  <p className="text-sm" style={{ color: '#3A5A85' }}>{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1F3B60' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white mb-4">
              How It Works
            </h2>
            <div className="divider-gold mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorksSteps.map((item) => (
              <div key={item.step} className="text-center">
                <div className="step-circle mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="font-display font-bold text-xl text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-white/70 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / Why Us Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block font-semibold text-xs tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full" style={{ color: '#D6A94F', backgroundColor: 'rgba(214, 169, 79, 0.1)', border: '1px solid rgba(214, 169, 79, 0.2)' }}>
              Why Choose Us
            </span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4" style={{ color: '#1F3B60' }}>
              Why Homeowners Trust Pacific Crest
            </h2>
            <p className="text-lg mt-4 max-w-2xl mx-auto" style={{ color: '#3A5A85' }}>
              We've built our reputation on quality, transparency, and exceptional service throughout Los Angeles.
            </p>
            <div className="divider-gold mx-auto mt-6" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {trustPoints.map((point) => {
              const IconComponent = point.icon;
              return (
                <div key={point.id} className="bg-white rounded-2xl p-6 shadow-md text-center hover:shadow-lg transition-shadow duration-300">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                    <IconComponent className="w-6 h-6" style={{ color: '#1F3B60' }} />
                  </div>
                  <h3 className="font-display font-bold text-lg mb-2" style={{ color: '#1F3B60' }}>{point.title}</h3>
                  <p className="text-sm" style={{ color: '#3A5A85' }}>{point.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="get-quote" className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1F3B60' }}>
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block font-semibold text-xs tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full" style={{ color: '#D6A94F', backgroundColor: 'rgba(214, 169, 79, 0.1)', border: '1px solid rgba(214, 169, 79, 0.2)' }}>
              Free Estimate
            </span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white mb-4">
              Request Your Free Renovation Quote
            </h2>
            <p className="text-lg text-white/70 max-w-xl mx-auto">
              Tell us about your project and receive a detailed estimate within 24 hours.
            </p>
            <div className="divider-gold mx-auto mt-6" />
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
            {isSubmitted ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#D6A94F' }} />
                <h3 className="font-display font-bold text-2xl mb-2" style={{ color: '#1F3B60' }}>
                  Thank You!
                </h3>
                <p style={{ color: '#3A5A85' }}>
                  We've received your project details and will send you a detailed estimate within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                {/* First Name & Last Name */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-medium mb-1" style={{ color: '#1F3B60' }}>
                      First Name <span style={{ color: '#c00' }}>*</span>
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      {...form.register("first_name")}
                      placeholder="First name"
                      className={`${inputBaseClass} ${form.formState.errors.first_name ? 'border-red-500' : ''}`}
                      style={{ color: '#1F3B60' }}
                    />
                    {form.formState.errors.first_name && (
                      <p className="text-red-500 text-xs mt-1">{form.formState.errors.first_name.message}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="last_name" className="block text-sm font-medium mb-1" style={{ color: '#1F3B60' }}>
                      Last Name <span style={{ color: '#c00' }}>*</span>
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      {...form.register("last_name")}
                      placeholder="Last name"
                      className={`${inputBaseClass} ${form.formState.errors.last_name ? 'border-red-500' : ''}`}
                      style={{ color: '#1F3B60' }}
                    />
                    {form.formState.errors.last_name && (
                      <p className="text-red-500 text-xs mt-1">{form.formState.errors.last_name.message}</p>
                    )}
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-1" style={{ color: '#1F3B60' }}>
                      Email <span style={{ color: '#c00' }}>*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      {...form.register("email")}
                      placeholder="you@example.com"
                      className={`${inputBaseClass} ${form.formState.errors.email ? 'border-red-500' : ''}`}
                      style={{ color: '#1F3B60' }}
                    />
                    {form.formState.errors.email && (
                      <p className="text-red-500 text-xs mt-1">{form.formState.errors.email.message}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="phone_number" className="block text-sm font-medium mb-1" style={{ color: '#1F3B60' }}>
                      Phone <span style={{ color: '#c00' }}>*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone_number"
                      {...form.register("phone_number")}
                      placeholder="(555) 123-4567"
                      className={`${inputBaseClass} ${form.formState.errors.phone_number ? 'border-red-500' : ''}`}
                      style={{ color: '#1F3B60' }}
                    />
                    {form.formState.errors.phone_number && (
                      <p className="text-red-500 text-xs mt-1">{form.formState.errors.phone_number.message}</p>
                    )}
                  </div>
                </div>

                {/* Property Address */}
                <div>
                  <label htmlFor="property_address" className="block text-sm font-medium mb-1" style={{ color: '#1F3B60' }}>
                    Property Address <span style={{ color: '#c00' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="property_address"
                    {...form.register("property_address")}
                    placeholder="123 Main St, Los Angeles, CA"
                    className={`${inputBaseClass} ${form.formState.errors.property_address ? 'border-red-500' : ''}`}
                    style={{ color: '#1F3B60' }}
                  />
                  {form.formState.errors.property_address && (
                    <p className="text-red-500 text-xs mt-1">{form.formState.errors.property_address.message}</p>
                  )}
                </div>

                {/* Renovation Types - Multi-select checkboxes */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F3B60' }}>
                    What type of renovation are you interested in? <span style={{ color: '#c00' }}>*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {renovationTypeOptions.map((type) => (
                      <label
                        key={type}
                        className={`flex items-center space-x-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${enrichment.renovationTypes.includes(type) ? '' : 'border-gray-300'}`}
                        style={enrichment.renovationTypes.includes(type) ? {
                          borderColor: '#D6A94F',
                          backgroundColor: 'rgba(214, 169, 79, 0.08)'
                        } : {}}
                      >
                        <input
                          type="checkbox"
                          checked={enrichment.renovationTypes.includes(type)}
                          onChange={() => toggleRenovationType(type)}
                          className="w-4 h-4 rounded"
                          style={{ accentColor: '#D6A94F' }}
                        />
                        <span className="text-sm" style={{ color: '#1F3B60' }}>{type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Budget, Timeline, Condition dropdowns */}
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="budget" className="block text-sm font-medium mb-1" style={{ color: '#1F3B60' }}>
                      Budget Range
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={enrichment.budget}
                      onChange={handleEnrichmentChange}
                      className={`${inputBaseClass} bg-white`}
                      style={{ color: '#1F3B60' }}
                    >
                      {budgetOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="timeline" className="block text-sm font-medium mb-1" style={{ color: '#1F3B60' }}>
                      Timeline
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={enrichment.timeline}
                      onChange={handleEnrichmentChange}
                      className={`${inputBaseClass} bg-white`}
                      style={{ color: '#1F3B60' }}
                    >
                      {timelineOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="condition" className="block text-sm font-medium mb-1" style={{ color: '#1F3B60' }}>
                      Property Condition
                    </label>
                    <select
                      id="condition"
                      name="condition"
                      value={enrichment.condition}
                      onChange={handleEnrichmentChange}
                      className={`${inputBaseClass} bg-white`}
                      style={{ color: '#1F3B60' }}
                    >
                      {conditionOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Project Description */}
                <div>
                  <label htmlFor="projectDescription" className="block text-sm font-medium mb-1" style={{ color: '#1F3B60' }}>
                    Tell Us About Your Project
                  </label>
                  <textarea
                    id="projectDescription"
                    name="projectDescription"
                    value={enrichment.projectDescription}
                    onChange={handleEnrichmentChange}
                    rows={4}
                    placeholder="Describe your renovation goals, specific areas of the home, any preferences or concerns..."
                    className={`${inputBaseClass} resize-none`}
                    style={{ color: '#1F3B60' }}
                  />
                </div>

                {/* SMS Consent */}
                <div className="flex items-start space-x-3 pt-2">
                  <input
                    type="checkbox"
                    checked={form.watch("sms_consent")}
                    onChange={(e) => form.setValue("sms_consent", e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-[#D6A94F]"
                    style={{ accentColor: '#D6A94F' }}
                  />
                  <label className="text-xs leading-relaxed" style={{ color: '#3A5A85' }}>
                    By checking this box, I agree to receive text messages from Pacific Crest Properties regarding my property inquiry, appointment scheduling, and related real estate services. Consent is not a condition of purchase. Message frequency varies. Message and data rates may apply. You may opt out at any time by replying STOP. For assistance, reply HELP. View our{' '}
                    <Link to="/privacy-policy" className="underline font-medium" style={{ color: '#D6A94F' }}>
                      Privacy Policy
                    </Link>{' '}
                    and{' '}
                    <Link to="/terms-and-conditions" className="underline font-medium" style={{ color: '#D6A94F' }}>
                      Terms &amp; Conditions
                    </Link>.
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={form.formState.isSubmitting}
                  className="btn-gold w-full inline-flex items-center justify-center space-x-2 text-lg"
                  style={{ opacity: form.formState.isSubmitting ? 0.7 : 1 }}
                >
                  {form.formState.isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>Get My Free Quote</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block font-semibold text-xs tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full" style={{ color: '#D6A94F', backgroundColor: 'rgba(214, 169, 79, 0.1)', border: '1px solid rgba(214, 169, 79, 0.2)' }}>
              Our Work
            </span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4" style={{ color: '#1F3B60' }}>
              Recent Renovation Outcomes
            </h2>
            <p className="text-lg mt-4 max-w-2xl mx-auto" style={{ color: '#3A5A85' }}>
              See what LA homeowners are saying about their renovation experiences with Pacific Crest.
            </p>
            <div className="divider-gold mx-auto mt-6" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {testimonials.map((item) => (
              <div key={item.id} className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center space-x-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" style={{ color: '#D6A94F' }} />
                  ))}
                </div>
                <h3 className="font-display font-bold text-lg mb-1" style={{ color: '#1F3B60' }}>
                  {item.title}
                </h3>
                <p className="text-xs font-medium mb-3" style={{ color: '#D6A94F' }}>
                  {item.location}
                </p>
                <p className="text-sm leading-relaxed" style={{ color: '#3A5A85' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <div className="inline-flex items-center space-x-2 px-6 py-3 rounded-full" style={{ backgroundColor: 'rgba(31, 59, 96, 0.08)' }}>
              <Star className="w-5 h-5 fill-current" style={{ color: '#D6A94F' }} />
              <span className="font-bold text-lg" style={{ color: '#1F3B60' }}>4.9/5</span>
              <span className="text-sm" style={{ color: '#3A5A85' }}>from 200+ reviews</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1F3B60' }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block font-semibold text-xs tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full" style={{ color: '#D6A94F', backgroundColor: 'rgba(214, 169, 79, 0.1)', border: '1px solid rgba(214, 169, 79, 0.2)' }}>
              FAQ
            </span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-white/70 max-w-xl mx-auto">
              Find answers to common questions about our renovation services.
            </p>
            <div className="divider-gold mx-auto mt-6" />
          </div>

          <div className="space-y-3">
            {faqItems.map((faq) => (
              <div
                key={faq.id}
                className="rounded-xl overflow-hidden transition-colors"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
              >
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                >
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  {openFaqs.has(faq.id) ? (
                    <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: '#D6A94F' }} />
                  ) : (
                    <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: '#D6A94F' }} />
                  )}
                </button>
                {openFaqs.has(faq.id) && (
                  <div className="px-6 pb-4">
                    <p className="text-white/70 leading-relaxed text-sm">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-white/60 mb-2">Still have questions?</p>
            <a
              href="tel:+13105966534"
              className="inline-flex items-center space-x-2 font-semibold text-lg"
              style={{ color: '#D6A94F' }}
            >
              <Phone className="w-5 h-5" />
              <span>Call Us: (310) 596-6534</span>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4" style={{ color: '#1F3B60' }}>
            Ready to Transform Your Home?
          </h2>
          <p className="text-lg mb-8" style={{ color: '#3A5A85' }}>
            Get a detailed renovation quote with transparent, itemized pricing.
          </p>
          <a
            href="#get-quote"
            onClick={scrollToForm}
            className="btn-gold inline-flex items-center space-x-2 text-lg group"
          >
            <Wrench className="w-5 h-5" />
            <span>Request a Quote</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/services" className="text-sm font-medium transition-colors" style={{ color: '#3A5A85' }}>
              &larr; Back to Services
            </Link>
            <Link to="/sell-your-home" className="text-sm font-medium transition-colors" style={{ color: '#D6A94F' }}>
              Explore Property Offers &rarr;
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RenovationPage;
